import { memo, useCallback } from "react";

import type { ForNodeType } from "@/components/features/_model/types/auto-generator-for-view-model";
import { EditGuideTooltip } from "@/components/features/items/edit/_shared/ui/components/system/tooltip/edit-guide-tooltip";

import { Button } from "@/components/ui/button/button";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";

import { CloseIcon } from "./close-icon-wrapper";

type Props = {
	title: string;
	helpContent?: string;
	sectionIndex: number;
	emails: Array<ForNodeType<string>>;
	disabled?: boolean;

	onAppendEmail: () => void;
	onRemoveEmail: (index: number) => void;
	onChangeEmail: (index: number, value: string) => void;
};

export const NotificationSettingForEmailForm = memo(
	({
		title,
		helpContent,
		sectionIndex,
		emails,
		disabled,

		onAppendEmail,
		onRemoveEmail,
		onChangeEmail,
	}: Props) => {
		const handleClickAppendEmail = useCallback(() => {
			onAppendEmail();
		}, [onAppendEmail]);

		const handleClickRemoveEmail = useCallback(
			(index: number) => () => {
				onRemoveEmail(index);
			},
			[onRemoveEmail],
		);

		const handleChangeEmail = useCallback(
			(index: number) => (value: string) => {
				onChangeEmail(index, value);
			},
			[onChangeEmail],
		);

		return (
			<div className="flex w-full flex-col gap-y-4 laptop:flex-row laptop:gap-x-6">
				<div className="grid w-full laptop:max-w-56 laptop:flex-none">
					<div className="flex items-center gap-x-2">
						<p className="text-sm font-semibold">Q{sectionIndex}</p>
						<h5>{title}の通知</h5>
						<EditGuideTooltip guide={helpContent} />
					</div>
				</div>
				<div className="min-w-0 flex-1">
					{emails.length === 0 ? (
						<Button
							variant="outline"
							size="lg"
							className="w-full laptop:w-auto"
							onClick={handleClickAppendEmail}
						>
							メールアドレスを設定する
						</Button>
					) : (
						<div className="grid-rows grid gap-y-4">
							<div className="grid-rows grid gap-y-4">
								{emails.map((email, index) => (
									<div key={index}>
										<div className="flex items-center gap-x-3">
											<div className="min-w-0 grow">
												<InputTextField
													type="email"
													value={email.value}
													handleChange={handleChangeEmail(index)}
													errorMessage={email.error}
												/>
											</div>
											<div className="flex-none" onClick={handleClickRemoveEmail(index)}>
												<CloseIcon />
											</div>
										</div>
									</div>
								))}
							</div>
							{emails.length < 3 && (
								<div>
									<Button
										variant="outline"
										disabled={disabled}
										size="lg"
										className="w-full laptop:w-auto"
										onClick={handleClickAppendEmail}
									>
										メールアドレスを追加する
									</Button>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		);
	},
);
